/* LegalPolicyPage.jsx — pagina dinamica /legal/:slug per le informative GDPR
 * collegate ai consensi (cliccabili dai checkbox dei wizard).
 *
 * Layout: breadcrumb + titolo + corpo (placeholder con struttura GDPR-compliant
 * art. 13 GDPR). Pulsante "Stampa" per export PDF cartaceo.
 *
 * I 9 slug supportati:
 *   privacy, marketing, profilazione, sanitari, mup,
 *   cookie, condizioni, promo-eventi, intermediario-rui
 *
 * Tracking apertura: POST /api/public/legal/track-view (audit GDPR compliance).
 */
import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import LegalDocPage from "./legal/LegalDocPage";
import { LEGAL_DOCS as COMPLIANCE_DOCS } from "./legal/legalDocs";
import axios from "axios";
import { Printer, ArrowLeft, ShieldCheck } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const POLICY_VERSION = "v2026.05";
const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/legal`;

const POLICIES = {
  privacy: {
    title: "Informativa Privacy (art. 13 GDPR)",
    intro: "Informativa sul trattamento dei dati personali resa ai sensi degli artt. 13-14 del Regolamento (UE) 2016/679 (GDPR) e del D.Lgs. 196/2003 e ss.mm.ii.",
    sections: [
      ["Titolare del trattamento", "CHRISTIAN E CIRO COLUCCI S.A.S. — RUI A000464752 — P.IVA 07618011212 — sede legale in Via Armando Diaz 182/L, 80055 Portici (NA). Email: privacy@colucciassicuratori.com"],
      ["Tipologie di dati trattati", "Dati anagrafici e di contatto (nome, cognome, codice fiscale, indirizzo, email, telefono), dati relativi al rischio assicurato (veicolo, immobile, attività professionale, condizioni di salute ove richiesti per le sole polizze vita/salute), dati di profilazione assicurativa."],
      ["Finalità", "a) gestione della richiesta di preventivo e successivo rapporto contrattuale; b) adempimenti contabili, fiscali e di legge (D.Lgs. 209/2005, IVASS); c) prevenzione frodi; d) gestione sinistri; e) marketing diretto (solo previo consenso opt-in)."],
      ["Base giuridica", "Art. 6.1.b (esecuzione contratto), 6.1.c (obblighi di legge), 6.1.f (interesse legittimo per prevenzione frodi), 6.1.a (consenso esplicito per marketing/profilazione/dati sanitari art.9)."],
      ["Conservazione", "10 anni dalla cessazione del rapporto contrattuale per finalità fiscali e antiriciclaggio. 2 anni per marketing salvo revoca anticipata."],
      ["Diritti dell'interessato (artt. 15-22 GDPR)", "Accesso, rettifica, cancellazione, limitazione, portabilità, opposizione, revoca del consenso, reclamo al Garante (www.garanteprivacy.it). Per esercitare i diritti scrivere a privacy@colucciassicuratori.it."],
      ["DPO", "Data Protection Officer raggiungibile via email all'indirizzo dpo@colucciassicuratori.it."],
    ],
  },
  marketing: {
    title: "Informativa Marketing Diretto",
    intro: "Informativa specifica per il trattamento dei dati personali per finalità di marketing diretto, ai sensi dell'art. 13 GDPR.",
    sections: [
      ["Finalità", "Invio di comunicazioni commerciali, promozionali e di marketing — anche personalizzate — relative a prodotti assicurativi, eventi, newsletter, offerte riservate."],
      ["Modalità", "Email, SMS, WhatsApp, telefono, posta cartacea, notifiche push web/app."],
      ["Base giuridica", "Art. 6.1.a GDPR — consenso esplicito e revocabile in qualsiasi momento."],
      ["Conservazione", "24 mesi dal rilascio del consenso, salvo revoca anticipata."],
      ["Revoca", "In ogni email è presente link di disiscrizione one-click. È possibile revocare scrivendo a privacy@colucciassicuratori.it o dalla propria Area Cliente."],
      ["Profilazione", "Il trattamento NON include profilazione automatizzata salvo consenso ulteriore specifico (vedi informativa profilazione)."],
    ],
  },
  profilazione: {
    title: "Informativa Profilazione",
    intro: "Trattamento dei dati personali con finalità di profilazione, ai sensi dell'art. 22 GDPR.",
    sections: [
      ["Finalità", "Analisi automatizzata delle abitudini di consumo, della storia assicurativa, della composizione del nucleo familiare e di altri parametri al fine di proporre prodotti personalizzati e tariffe dedicate."],
      ["Logica del trattamento", "Modelli statistici/ML che combinano dati anagrafici, contrattuali e di comportamento. NESSUNA decisione legalmente vincolante è presa in modo esclusivamente automatizzato senza intervento umano."],
      ["Conseguenze", "Riceverai proposte commerciali costruite sulla tua specifica situazione. La mancata adesione NON pregiudica la possibilità di stipulare polizze a tariffe standard."],
      ["Base giuridica", "Art. 6.1.a + art. 22.2.c GDPR — consenso esplicito."],
      ["Diritti specifici", "Diritto a ottenere intervento umano, a esprimere la propria opinione e a contestare la decisione (art. 22.3)."],
      ["Conservazione", "24 mesi dal consenso o fino a revoca."],
    ],
  },
  sanitari: {
    title: "Informativa Trattamento Dati Sanitari (art. 9 GDPR)",
    intro: "Informativa specifica per il trattamento di dati appartenenti a categorie particolari (dati sanitari), ai sensi dell'art. 9 GDPR.",
    sections: [
      ["Tipologie di dati sanitari", "Patologie pregresse, esami clinici, peso/altezza/BMI, valori pressori, colesterolo, abitudini al fumo, attività sportiva, terapie farmacologiche, dati relativi al nucleo familiare per polizze collettive."],
      ["Finalità", "Esclusivamente: a) valutazione del rischio assicurativo per polizze vita/salute/infortuni; b) emissione e gestione del contratto; c) gestione sinistri sanitari."],
      ["Base giuridica", "Art. 9.2.a GDPR — consenso esplicito separato dell'interessato — combinato con esigenza di esecuzione del contratto assicurativo (art. 6.1.b)."],
      ["Modalità di trattamento", "I dati sanitari sono cifrati AES-256-GCM in archivio e accessibili solo a personale espressamente autorizzato (medico fiduciario, periti delegati). Nessuna comunicazione a terzi non autorizzati."],
      ["Conservazione", "10 anni dalla cessazione contrattuale per esigenze di gestione sinistri e contenzioso."],
      ["Revoca", "La revoca interrompe l'iter di emissione/gestione polizza per le coperture che necessitano della valutazione sanitaria."],
    ],
  },
  mup: {
    title: "Modulo Unico di Proposta (MUP)",
    intro: "Modulo Unico di Proposta ai sensi del Regolamento IVASS n. 41/2018 e D.Lgs. 209/2005 (Codice delle Assicurazioni Private).",
    sections: [
      ["Cos'è il MUP", "Documento standardizzato che accompagna la proposta di polizza con tutte le informazioni precontrattuali obbligatorie: rischio coperto, durata, premio, franchigie, esclusioni, diritto di recesso."],
      ["Funzione", "Garantisce trasparenza precontrattuale: l'assicurato ha accesso a tutti i dati necessari PRIMA di sottoscrivere il contratto."],
      ["Cosa contiene", "1) Anagrafica intermediario e RUI; 2) Compagnia emittente e ramo; 3) Garanzie e massimali; 4) Premio totale e suddivisione per garanzia; 5) Modalità di pagamento; 6) Durata e rinnovo; 7) Diritto di recesso (14 giorni per polizze a distanza)."],
      ["Conservazione documento", "Copia inviata al cliente via email e disponibile permanentemente nell'Area Cliente."],
      ["Riferimenti normativi", "Reg. IVASS 41/2018 art. 7-9; D.Lgs. 209/2005 artt. 165-185."],
    ],
  },
  cookie: {
    title: "Cookie Policy",
    intro: "Informativa sull'uso dei cookie e di tecnologie similari ai sensi del Provvedimento del Garante 10 giugno 2021.",
    sections: [
      ["Tipologie utilizzate", "Cookie tecnici (sempre attivi, necessari al funzionamento del sito) · Cookie analitici anonimizzati (attivabili con consenso) · Cookie di profilazione (attivabili con consenso esplicito)."],
      ["Cookie di terze parti", "Utilizziamo SOLO cookie tecnici essenziali. NON utilizziamo cookie di terze parti per profilazione."],
      ["Gestione preferenze", "È possibile modificare le proprie preferenze in qualsiasi momento dal banner cookie in fondo alla pagina o dalle impostazioni del browser."],
      ["Conservazione", "Cookie di sessione: cancellati alla chiusura del browser. Cookie persistenti: max 12 mesi."],
    ],
  },
  condizioni: {
    title: "Condizioni d'Uso del Servizio",
    intro: "Termini e condizioni che regolano l'utilizzo del sito web e dei servizi digitali di Colucci Assicuratori.",
    sections: [
      ["Servizi offerti", "Preventivi assicurativi online, Area Cliente per la gestione delle polizze, comunicazioni digitali, pagamenti online, firma elettronica."],
      ["Obblighi dell'utente", "Fornire dati veritieri e aggiornati; non utilizzare il servizio per scopi illeciti; rispettare i diritti di proprietà intellettuale; non tentare accessi non autorizzati."],
      ["Limitazioni di responsabilità", "Il preventivo online costituisce stima indicativa e NON impegno contrattuale. La polizza definitiva richiede emissione formale e pagamento del premio."],
      ["Foro competente", "Foro di Torre Annunziata per controversie ai sensi degli artt. 33-34 D.Lgs. 206/2005 (Codice del Consumo)."],
    ],
  },
  "promo-eventi": {
    title: "Informativa Trattamenti per Eventi Promozionali",
    intro: "Informativa specifica per il trattamento dei dati personali raccolti tramite form di iscrizione a eventi promozionali (es. fiere, congressi, stand di consulenza).",
    sections: [
      ["Finalità", "Gestione dell'iscrizione, conferma di partecipazione, invio promemoria evento, distribuzione di omaggi/gadget, statistiche aggregate post-evento."],
      ["Tipo di iniziativa", "Operazione promozionale a carattere informativo SENZA caratteristiche di concorso a premi (DPR 430/2001 esclude operazioni con sola finalità di fidelizzazione clientela e omaggistica di modesto valore)."],
      ["Base giuridica", "Art. 6.1.a GDPR — consenso esplicito."],
      ["Conservazione", "6 mesi dall'evento. I dati possono essere conservati oltre solo se confluiscono in iniziative di marketing per cui sia stato dato consenso autonomo."],
      ["Diritti", "Come da informativa privacy generale (revoca, cancellazione, accesso)."],
    ],
  },
  "intermediario-rui": {
    title: "Informativa Intermediario Assicurativo (RUI)",
    intro: "Informativa precontrattuale sull'intermediario assicurativo ai sensi del Regolamento IVASS n. 40/2018.",
    sections: [
      ["Denominazione", "CHRISTIAN E CIRO COLUCCI S.A.S."],
      ["Iscrizione RUI", "Numero A000464752 — Sezione A (agenti) — data iscrizione consultabile su www.ivass.it"],
      ["Recapiti", "Sede legale: Via Armando Diaz 182/L, 80055 Portici (NA) — Tel. 081/7611000 — Email info@colucciassicuratori.com — PEC colucciassicuratori@pec.it"],
      ["Imprese mandanti", "Reale Mutua Assicurazioni, Generali Italia, Allianz, UnipolSai, Helvetia, Groupama, Cattolica, BlueAssistance e altre compagnie convenzionate (elenco aggiornato disponibile in agenzia)."],
      ["Tipologia di consulenza", "L'intermediario opera SU MANDATO delle imprese sopra indicate ai sensi dell'art. 119 D.Lgs. 209/2005."],
      ["Reclami", "Reclami scritti possono essere inviati a reclami@colucciassicuratori.it. Se il riscontro non è soddisfacente entro 45 gg è possibile rivolgersi a IVASS (www.ivass.it) o all'Arbitro Assicurativo."],
      ["Garanzie", "L'intermediario dispone di polizza RC professionale ai sensi del Reg. IVASS 40/2018 art. 12."],
    ],
  },
};

export default function LegalPolicyPage() {
  const { slug } = useParams();
  const isComplianceDoc = slug && COMPLIANCE_DOCS[slug];
  const policy = isComplianceDoc ? null : POLICIES[slug];

  useEffect(() => {
    if (!policy) return;
    // Tracking apertura (GDPR audit) — fire & forget
    axios.post(`${API}/track-view`, {
      policy_slug: slug,
      policy_version: POLICY_VERSION,
      referrer: typeof window !== "undefined" ? window.document.referrer : null,
    }).catch(() => { /* tracking non bloccante */ });
  }, [slug, policy]);

  // FASE 4.2.11.5 — Compliance IVASS MUP/DIP/AI: delega al renderer dedicato
  if (isComplianceDoc) return <LegalDocPage />;
  if (!policy) return <Navigate to="/" replace />;

  const printPage = () => {
    if (typeof window !== "undefined") window.print();
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50"
         data-testid={`legal-page-${slug}`}>
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 py-8 sm:py-12">
        <nav className="text-xs text-slate-500 mb-4 flex items-center gap-2"
             aria-label="Breadcrumb">
          <Link to="/" className="hover:text-emerald-700 inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Home
          </Link>
          <span aria-hidden="true">·</span>
          <span>Informative legali</span>
          <span aria-hidden="true">·</span>
          <span className="font-bold text-slate-700">{policy.title}</span>
        </nav>

        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10">
          <header className="flex items-start justify-between gap-4 mb-6 pb-5 border-b border-slate-200">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-7 h-7 text-emerald-600 mt-1 shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-[#163321]"
                    data-testid="legal-title">
                  {policy.title}
                </h1>
                <p className="text-xs text-slate-500 mt-1">
                  Versione <b>{POLICY_VERSION}</b> · Ultimo aggiornamento febbraio 2026
                </p>
              </div>
            </div>
            <button onClick={printPage} type="button"
                    data-testid="legal-print-btn"
                    aria-label="Stampa o salva PDF dell'informativa"
                    className="hidden sm:inline-flex items-center gap-1.5 min-h-[40px] px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
              <Printer className="w-4 h-4" /> Stampa / PDF
            </button>
          </header>

          <p className="text-sm text-slate-700 mb-6 leading-relaxed">
            {policy.intro}
          </p>

          <div className="space-y-5">
            {policy.sections.map(([h, body], i) => (
              <section key={i} data-testid={`legal-section-${i}`}>
                <h2 className="text-base sm:text-lg font-bold text-[#163321] mb-1.5">
                  {i + 1}. {h}
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
              </section>
            ))}
          </div>

          <footer className="mt-8 pt-5 border-t border-slate-200 text-xs text-slate-500">
            <p>
              <b>Titolare del trattamento:</b> CHRISTIAN E CIRO COLUCCI S.A.S.
              · RUI A000464752 · P.IVA 07618011212 · Sede legale: Via Armando
              Diaz 182/L, 80055 Portici (NA) · Email: privacy@colucciassicuratori.com
              · DPO: dpo@colucciassicuratori.com
            </p>
            <p className="mt-2 italic text-amber-700">
              Questo documento rappresenta una versione preliminare in attesa di
              validazione finale da parte del consulente legale aziendale.
              I contenuti sono allineati ai requisiti GDPR e IVASS ma potrebbero
              essere oggetto di revisione editoriale.
            </p>
          </footer>
        </article>

        <div className="text-center mt-6">
          <Link to="/"
                data-testid="legal-back-home"
                className="inline-flex items-center gap-2 text-sm text-emerald-700 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
